<script lang="ts">
    import { onDestroy } from 'svelte'
    import { fade } from 'svelte/transition'

    import Icon from '@iconify/svelte'

    import { gettext as _ } from 'hydria/services/gettext'
    import { makeSphinxTag } from 'hydria/utils/sphinx'

    import { loadFrequentTags, type FTag } from 'admin/helpers/tags'

    interface Props {
        filter?: (text: string) => void
        close: () => void
    }

    let { filter, close }: Props = $props()

    $effect(() => {
        if (!document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.add('modal-open')
        } else if (document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })

    onDestroy(() => {
        if (document.documentElement.classList.contains('modal-open')) document.documentElement.classList.remove('modal-open')
    })

    function applyCira(tag: string, reverse: boolean) {
        let text = makeSphinxTag('cira', tag)
        if (reverse) text = `:system:cira: -${text}`
        if (filter) filter(text)
    }

    function applyService(tag: string) {
        let text = makeSphinxTag('arcservice', tag)
        if (filter) filter(text)
    }

    function applyBoolean(tag: string, bool: boolean) {
        let text = makeSphinxTag(tag, bool)
        if (filter) filter(text)
    }

    function applyTag(tag: string) {
        let text = `tag:${tag}`
        if (filter) filter(text)
    }

    let globalFrequentTags: FTag[] | undefined = $state(undefined)

    loadFrequentTags('domain').then((r) => {
        globalFrequentTags = r
    })
</script>

<style>
    .modal-dialog {
        --bs-modal-width: 85vw;
    }
    ul.dom-search-sample {
        columns: 28em auto;
    }
    li {
        margin: 0.1rem 0;
    }
</style>

<div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
    <div class="modal-dialog">
        <div class="modal-content">
            <div class="modal-header text-bg-primary">
                <div>Filtres spéciaux</div>
            </div>
            <div class="modal-body">
                <p>Comme texte, on peut entrer n’importe quel tag interne. Ces tags commencent et terminent par “:”.</p>
                <p>On peut aussi chercher négativement en collant un “-”, mais dû à Sphinx, <em>ça prend au moins un mot positif</em>.</p>
                <p>De plus, les tags ont les “:” et espaces enlevés dans les clés. Donc <code>serverUpdateProhibited:change registrant</code> devient <code>:cira:serverupdateprihivitedchangeregistrant:</code>.</p>
                <p>Exemples de négatif : <code>:system:cira: -:cira:clienthold:</code>.</p>
                <p>Status CIRA :</p>
                <ul class="dom-search-sample">
                    {#each ['clientHold', 'serverHold', 'clientDeleteProhibited', 'clientRenewProhibited', 'clientTransferProhibited', 'clientUpdateProhibited', 'serverDeleteProhibited', 'serverRenewProhibited', 'serverTransferProhibited', 'serverUpdateProhibited', 'serverUpdateProhibited:all', 'serverUpdateProhibited:change registrant', 'pendingCreate', 'pendingDelete', 'pendingRenew', 'pendingTransfer', 'pendingUpdate', 'inactive'] as x}
                        <li>
                            <code>{x}</code>
                            <div class="btn-group btn-group-sm">
                                <button type="button" class="btn btn-outline-secondary" onclick={() => applyCira(x, false)}><Icon inline icon="mdi:plus" /></button>
                                <button type="button" class="btn btn-outline-secondary" onclick={() => applyCira(x, true)}><Icon inline icon="mdi:minus" /></button>
                            </div>
                        </li>
                    {/each}
                </ul>
                <p>Services ARC :</p>
                <ul class="dom-search-sample">
                    {#each ['cpanel-ns', 'cpanel-mx', 'cpanel-web', 'webdomain-ns', 'hydria-01', 'hydria-02', 'hydria-03', 'hydria-04', 'hydria-05', 'hydria-06', 'hydria-07', 'hydria-08', 'hydria-09', 'hydria-10', 'hydria-11', 'hydria-12'] as x}
                        <li>
                            <code>{x}</code>
                            <div class="btn-group btn-group-sm">
                                <button class="btn btn-outline-secondary" onclick={() => applyService(x)}><Icon inline icon="mdi:check" /></button>
                            </div>
                        </li>
                    {/each}
                </ul>
                <p>Autre :</p>
                <ul class="dom-search-sample">
                    {#each ['zonepublishable'] as x}
                        <li>
                            <code>:{x}:BOOL:</code>
                            <div class="btn-group btn-group-sm">
                                <button class="btn btn-outline-secondary" onclick={() => applyBoolean(x, true)}><Icon inline icon="mdi:check" /></button>
                                <button class="btn btn-outline-secondary" onclick={() => applyBoolean(x, false)}><Icon inline icon="mdi:close" /></button>
                            </div>
                        </li>
                    {/each}
                </ul>
                <p>« Tags » :</p>
                <ul class="dom-search-sample">
                    {#if globalFrequentTags}
                        {#if globalFrequentTags.length}
                            {#each globalFrequentTags as tag}
                                <li>
                                    <code>tag:{tag.id}</code>
                                    <div class="btn-group btn-group-sm">
                                        <button class="btn btn-outline-secondary" onclick={() => applyTag(tag.id)}><Icon inline icon="mdi:check" /></button>
                                    </div>
                                </li>
                            {/each}
                        {:else}
                            <li class="text-italic">(aucun tag)</li>
                        {/if}
                    {:else}
                        <li>
                            <div class="spinner-border me-3" role="status" aria-hidden="true"></div>
                        </li>
                    {/if}
                </ul>
            </div>
            <div class="modal-footer text-bg-light">
                <button class="btn btn-outline-secondary" onclick={() => close()}>{$_('Close')}</button>
            </div>
        </div>
    </div>
    <div
        class="modal-backdrop"
        aria-hidden="true"
        onclick={() => {
            close()
        }}
    ></div>
</div>
